import { googleSheetsApi } from '@bv/api/google-sheets';
import type { GoogleSheetsTokenParams } from '@bv/api/google-sheets';
import { useToast } from '@bv/composables/common/toast';
import { listenForGoogleSheetsOAuth } from '@bv/utils/google-sheets-oauth-channel';

import type { FieldValue, FieldValues, StepConfig, StepFormField } from '../../types/workflow';

/** Maximum time (ms) to wait for the OAuth popup before giving up */
const OAUTH_POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Extract tokens from the node's data object.
 * Returns null when any required token field is missing.
 */
const getTokensFromData = (data: FieldValues): GoogleSheetsTokenParams | null => {
  const accessToken = data.accessToken as string | undefined;
  const refreshToken = data.refreshToken as string | undefined;
  const expiryDate = (data.expiryDate || data.expiresAt) as number | undefined;

  if (!accessToken || !refreshToken || !expiryDate) {
    return null;
  }

  return { accessToken, refreshToken, expiryDate };
};

/**
 * Whether the user has connected their Google account.
 */
const isConnected = (data: FieldValues): boolean => {
  return !!getTokensFromData(data);
};

// ── Per-context caches ──────────────────────────────────────────────────────
// Keyed by relevant context to avoid cross-node data contamination.

/** Cache: accessToken → spreadsheet list */
const sheetsCache = new Map<string, Array<{ id: string; name: string }>>();

/** Cache: spreadsheetId → sub-sheet list */
const subSheetsCache = new Map<string, Array<{ title: string; sheetId: number }>>();

/** Cache: "spreadsheetId/sheetName" → column headers */
const columnsCache = new Map<string, string[]>();

/**
 * Processes tokens received from the OAuth callback popup and updates
 * the workflow node data through the store.
 */
const processOAuthTokens = (raw: string, params?: Record<string, unknown>, clearDependentFields = false): boolean => {
  try {
    const tokens = JSON.parse(raw);

    // Update node data through the store
    const store = (params as Record<string, any>)?.store;
    if (store?.selectedNode) {
      const updatedData: Record<string, unknown> = {
        ...store.selectedNode.data,
        accessToken: tokens.access_token || tokens.accessToken,
        refreshToken: tokens.refresh_token || tokens.refreshToken,
        expiryDate: Number(tokens.expiry_date || tokens.expiryDate),
        googleEmail: tokens.email || '',
      };

      if (clearDependentFields) {
        updatedData.spreadsheetId = '';
        updatedData.spreadsheetName = '';
        updatedData.sheetName = '';
        updatedData.rowValues = [];
      }

      store.updateNodeData(store.selectedNode.id, updatedData);
      return true;
    }
  } catch {
    // Ignore parse errors
  }
  return false;
};

/**
 * Opens the Google OAuth popup and listens for token delivery via BroadcastChannel
 * (posted by the callback popup when OAuth completes).
 * @param params - Must contain `store` (the workflow store instance)
 * @param clearDependentFields - If true, clears spreadsheet/sheet selections (used by reconnect)
 */
const openOAuthPopup = async (params?: Record<string, unknown>, clearDependentFields = false): Promise<void> => {
  const toast = useToast();
  const response = await googleSheetsApi.getAuthUrl();
  const authUrl = response.data;
  if (!authUrl) {
    return;
  }

  // Open OAuth popup centred on the current window
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  window.open(
    authUrl,
    'google-sheets-auth',
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
  );

  let stopListening: () => void = () => {};
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const cleanup = () => {
    stopListening();
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  stopListening = listenForGoogleSheetsOAuth(
    (raw) => {
      cleanup();
      processOAuthTokens(raw, params, clearDependentFields);
    },
    () => {
      cleanup();
      toast.error('Google Sheets connection was cancelled.');
    },
  );

  timeoutId = setTimeout(() => {
    cleanup();
  }, OAUTH_POLL_TIMEOUT_MS);
};

// ── Step config ──────────────────────────────────────────────────────────────

const addGoogleSheetRow: StepConfig = {
  // Design
  icon: 'addGoogleSheetRow',
  headerTextColor: '#006B43',
  headerColor: '#E3FFF3',
  title: 'Add Google Sheet Row',
  text: 'Message .....',
  category: 'Router',
  desc: 'Allows a new row to be created in a Google Sheets with variables added to specific columns. To successfully connect you must have edit access to the Sheet.',
  primaryKey: (parentValues: Record<string, any>) => {
    const sheetName = parentValues.spreadsheetName;
    const subSheet = parentValues.sheetName;
    if (sheetName && subSheet) {
      return `${sheetName} → ${subSheet}`;
    }
    if (sheetName) {
      return sheetName as string;
    }
    return 'Message .....';
  },

  // Connector configuration (Success / Failure)
  connectors: {
    GoogleSheetSuccess: {
      headerTextColor: '#4CAF50',
      headerColor: '#E8F5E9',
      title: 'Success',
      category: 'Connector',
    },
    GoogleSheetFailure: {
      headerTextColor: '#F44336',
      headerColor: '#FFEBEE',
      title: 'Failure',
      category: 'Connector',
    },
  },

  // Form fields
  formFields: [
    // ───── Section: Connect Google Account ─────
    {
      key: 'connectGoogleSheetsSection',
      type: 'accordion',
      label: 'Connect a Google Drive Spreadsheet :',
      defaultValue: true,
      fields: [
        // "Connect Google Sheets" button – shown when NOT connected
        {
          key: 'connectGoogleSheets',
          type: 'button',
          label: '',
          buttonText: 'Connect Google Sheets',
          buttonType: 'primary',
          show: (parentValues: FieldValues) => !isConnected(parentValues),
          onClick: async (
            _allValues: FieldValues,
            _allFields: Record<string, StepFormField>,
            params?: Record<string, unknown>,
          ) => {
            try {
              await openOAuthPopup(params);
            } catch (err: any) {
              const toast = useToast();
              const errorMessage = err?.message || 'Failed to connect to Google Sheets. Please try again.';
              toast.error(errorMessage);
            }
          },
        },

        // Email display – shown when connected
        {
          key: 'googleEmail',
          type: 'text',
          label: 'Email :',
          disabled: true,
          show: (parentValues: FieldValues) => isConnected(parentValues),
          validation: {
            required: false,
          },
        },

        // Reconnect button – shown when connected
        {
          key: 'reconnectGoogle',
          type: 'button',
          label: '',
          buttonText: '🔄 Reconnect',
          buttonType: 'secondary',
          show: (parentValues: FieldValues) => isConnected(parentValues),
          onClick: async (
            _allValues: FieldValues,
            _allFields: Record<string, StepFormField>,
            params?: Record<string, unknown>,
          ) => {
            try {
              await openOAuthPopup(params, true);
            } catch (err: any) {
              const toast = useToast();
              const errorMessage = err?.message || 'Failed to connect to Google Sheets. Please try again.';
              toast.error(errorMessage);
            }
          },
        },
      ],
    },

    // ───── Section: Sheet Selection ─────
    {
      key: 'sheetSection',
      type: 'accordion',
      label: 'Sheet :',
      defaultValue: true,
      show: (parentValues: FieldValues) => isConnected(parentValues),
      fields: [
        // Spreadsheet selection
        {
          key: 'spreadsheetId',
          type: 'select',
          label: 'Sheet Name',
          placeholder: 'Sheet Name',
          defaultValue: '',
          validation: {
            required: true,
          },
          options: (parentValues: FieldValues) => {
            const tokens = getTokensFromData(parentValues);
            if (!tokens) {
              return [];
            }
            const cacheKey = tokens.accessToken;
            const cached = sheetsCache.get(cacheKey);
            if (cached && cached.length > 0) {
              return cached.map((s) => ({
                label: s.name,
                value: s.id,
                name: s.name,
                id: s.id,
              }));
            }
            return async () => {
              try {
                const response = await googleSheetsApi.getSheets(tokens);
                const sheets = response.data?.sheets || [];
                sheetsCache.set(cacheKey, sheets);
                return sheets.map((s) => ({
                  label: s.name,
                  value: s.id,
                  name: s.name,
                  id: s.id,
                }));
              } catch {
                return [];
              }
            };
          },
          onChange: (value: FieldValue, allValues: FieldValues, allFields?: Record<string, any>) => {
            if (allFields && allFields.sheetName) {
              allFields.sheetName.show = !!value;
            }

            const previousId = (allValues as Record<string, any>)._previousSpreadsheetId;
            const hasExistingSheetName = !!(allValues as Record<string, any>).sheetName;

            // Prevent clearing data on spurious emits from Vuetify Select on remount
            if (previousId === value) {
              return;
            }
            if (previousId === undefined && hasExistingSheetName) {
              (allValues as Record<string, any>)._previousSpreadsheetId = value;
              return;
            }

            (allValues as Record<string, any>)._previousSpreadsheetId = value;

            // Save the selected spreadsheet name for display
            const tokens = getTokensFromData(allValues);
            const cacheKey = tokens?.accessToken || '';
            const cached = sheetsCache.get(cacheKey) || [];
            const sheet = cached.find((s) => s.id === value);
            if (sheet) {
              (allValues as Record<string, any>).spreadsheetName = sheet.name;
            }
            // Clear dependent fields
            (allValues as Record<string, any>).sheetName = '';
            (allValues as Record<string, any>).rowValues = [];
            // Clear stale sub-sheet / column caches for the previous spreadsheet
            if (previousId) {
              subSheetsCache.delete(previousId as string);
              // Clear all column caches for the previous spreadsheet
              Array.from(columnsCache.keys()).forEach((key) => {
                if (key.startsWith(`${previousId}/`)) {
                  columnsCache.delete(key);
                }
              });
            }
          },
        },

        // Sub-sheet (worksheet / tab) selection
        {
          key: 'sheetName',
          type: 'select',
          label: 'Sub Sheet',
          placeholder: 'Type something',
          defaultValue: '',
          dependsOn: ['spreadsheetId'],
          validation: {
            required: true,
          },
          options: (parentValues: FieldValues) => {
            const tokens = getTokensFromData(parentValues);
            const spreadsheetId = parentValues.spreadsheetId as string;
            if (!tokens || !spreadsheetId) {
              return [];
            }
            const cached = subSheetsCache.get(spreadsheetId);
            if (cached && cached.length > 0) {
              return cached.map((s) => ({
                label: s.title,
                value: s.title,
                name: s.title,
                id: s.title,
              }));
            }
            return async () => {
              try {
                const response = await googleSheetsApi.getSubSheets(spreadsheetId, tokens);
                const sheets = response.data?.sheets || [];
                const mapped = sheets.map((s) => ({
                  title: s.properties.title,
                  sheetId: s.properties.sheetId,
                }));
                subSheetsCache.set(spreadsheetId, mapped);
                return mapped.map((s) => ({
                  label: s.title,
                  value: s.title,
                  name: s.title,
                  id: s.title,
                }));
              } catch {
                return [];
              }
            };
          },
          onChange: async (value: FieldValue, allValues: FieldValues) => {
            const previousSheet = (allValues as Record<string, any>)._previousSheetName;
            const hasExistingColumns = (allValues as Record<string, any>).columnHeaders?.length > 0;

            // Prevent clearing data on spurious emits from Vuetify Select on remount
            if (previousSheet === value) {
              return;
            }
            if (previousSheet === undefined && hasExistingColumns) {
              (allValues as Record<string, any>)._previousSheetName = value;
              return;
            }

            (allValues as Record<string, any>)._previousSheetName = value;

            // Clear column mapping when sub-sheet changes
            (allValues as Record<string, any>).rowValues = [];
            (allValues as Record<string, any>).columnHeaders = [];
            (allValues as Record<string, any>)._isLoadingColumns = true;

            // Fetch column headers for the selected sub-sheet
            const tokens = getTokensFromData(allValues);
            const spreadsheetId = allValues.spreadsheetId as string;
            const sheetName = value as string;

            if (tokens && spreadsheetId && sheetName) {
              const colCacheKey = `${spreadsheetId}/${sheetName}`;
              const { useWorkflowStore } = await import('../../store/workflow');
              const store = useWorkflowStore();
              try {
                // Check column cache first
                const cachedCols = columnsCache.get(colCacheKey);
                let headers: string[];

                if (cachedCols && cachedCols.length > 0) {
                  headers = cachedCols;
                } else {
                  const response = await googleSheetsApi.getSubSheetData(spreadsheetId, sheetName, tokens);
                  headers = response.data?.values || [];
                  columnsCache.set(colCacheKey, headers);
                }

                // Auto-populate rowValues with fetched column headers
                const rowValues = headers.map((header: string, index: number) => ({
                  id: `col_${Date.now()}_${index}`,
                  columnName: header,
                  value: '',
                }));

                // Update store directly since onChange is async and the
                // synchronous store.updateNodeData has already been called
                if (store.selectedNode) {
                  const updatedData = {
                    ...store.selectedNode.data,
                    sheetName,
                    rowValues,
                    columnHeaders: headers,
                    _isLoadingColumns: false,
                  };
                  store.updateNodeData(store.selectedNode.id, updatedData);
                }
              } catch {
                columnsCache.delete(colCacheKey);
                if (store.selectedNode) {
                  const updatedData = {
                    ...store.selectedNode.data,
                    sheetName,
                    rowValues: [],
                    columnHeaders: [],
                    _isLoadingColumns: false,
                  };
                  store.updateNodeData(store.selectedNode.id, updatedData);
                }
              }
            } else {
              (allValues as Record<string, any>)._isLoadingColumns = false;
            }
          },
        },
      ],
    },

    // ───── Section: Column Mapping (When Headers Exist) ─────
    {
      key: 'columnsSection',
      type: 'accordion',
      label: 'Columns :',
      defaultValue: true,
      show: (parentValues: FieldValues) => {
        const headers = parentValues.columnHeaders as string[] | undefined;
        const isLoading = !!(parentValues as Record<string, any>)._isLoadingColumns;
        return isConnected(parentValues) && !!parentValues.sheetName && !!headers && headers.length > 0 && !isLoading;
      },
      fields: [
        {
          key: 'rowValues',
          type: 'formRepeater',
          defaultValue: [],
          dependsOn: ['sheetName', 'spreadsheetId', 'columnHeaders'],
          config: {
            maxItems: 100,
            minItems: 0,
            disableAdd: true,
            disableDelete: true,
            showLogicalOperator: false,
            fields: [
              {
                key: 'columnName',
                type: 'text',
                label: 'Column',
                placeholder: 'Column header',
                defaultValue: '',
                disabled: true,
                validation: {
                  required: false,
                },
              },
              {
                key: 'value',
                type: 'text',
                label: 'Value',
                placeholder: 'Type something',
                defaultValue: '',
                useVariables: true,
              },
            ],
          },
        },
      ],
    },

    // ───── Section: Loading Columns ─────
    {
      key: 'loadingColumnsSection',
      type: 'accordion',
      label: 'Columns :',
      defaultValue: true,
      show: (parentValues: FieldValues) => {
        return !!(parentValues as Record<string, any>)._isLoadingColumns;
      },
      fields: [
        {
          key: 'loadingMessage',
          type: 'button',
          label: '',
          buttonText: '⏳ Loading headers...',
          buttonType: 'secondary',
          disabled: true,
        },
      ],
    },

    // ───── Section: No Columns Message (When No Headers) ─────
    {
      key: 'noColumnsSection',
      type: 'accordion',
      label: 'Columns :',
      defaultValue: true,
      show: (parentValues: FieldValues) => {
        const headers = parentValues.columnHeaders as string[] | undefined;
        const isLoading = !!(parentValues as Record<string, any>)._isLoadingColumns;
        return (
          isConnected(parentValues) && !!parentValues.sheetName && (!headers || headers.length === 0) && !isLoading
        );
      },
      fields: [
        {
          key: 'noHeadersMessage',
          type: 'button',
          label: '',
          buttonText: "This sheet doesn't have any headers.",
          buttonType: 'secondary',
          disabled: true,
        },
      ],
    },
  ],
};

export default addGoogleSheetRow;
